import { useEffect, useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import type { useTableSearch } from '../hooks/useTableSearch'
import Search from '@/assets/icons/table-search.svg?react'
import { ChevronUp, ChevronDown, XClear } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { useDelayedVisibility } from '@/hooks/useDelayedVisibility'

export default function TableSearch({
  value,
  onChange,
  filenamesOnly = false,
  disabled = false,
  navigation,
  onPendingChange,
}: {
  value: string
  onChange: (value: string) => void
  filenamesOnly?: boolean
  disabled?: boolean
  navigation?: ReturnType<typeof useTableSearch>
  onPendingChange?: (pending: boolean) => void
}) {
  const formRef = useRef<HTMLFormElement>(null)
  const [popoverOpen, setPopoverOpen] = useState(true)
  const [draft, setDraft] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const restoreFocus = useRef(false)
  const text = draft ?? value
  const hasSearch = Boolean(value.trim())
  const hasEdits = text !== value
  const applied = hasSearch && !hasEdits
  const showSearching = useDelayedVisibility(Boolean(applied && navigation?.searching), { showDelayMs: 250, minDisplayMs: 0 })
  const showCounting = useDelayedVisibility(
    Boolean(applied && navigation && !navigation.searching && !navigation.error && !navigation.noMatches && navigation.total == null && !navigation.countError),
    { showDelayMs: 250, minDisplayMs: 0 }
  )
  useEffect(() => {
    onPendingChange?.(hasEdits)
  }, [hasEdits, onPendingChange])
  const label = filenamesOnly ? 'Search filenames' : 'Search in table'

  if (!expanded && !hasSearch) {
    return (
      <IconButton
        ref={(button) => {
          if (button && restoreFocus.current) {
            button.focus()
            restoreFocus.current = false
          }
        }}
        size="sm"
        variant="plain"
        color="black"
        icon={<Search />}
        aria-label={filenamesOnly ? 'Search source files' : 'Search table'}
        aria-expanded={false}
        tooltipText="Search"
        disabled={disabled}
        onClick={() => {
          setDraft(null)
          setExpanded(true)
        }}
      />
    )
  }

  return (
    <Popover open={Boolean(navigation && (popoverOpen || hasEdits) && (text.length || hasSearch))} onOpenChange={setPopoverOpen}>
      <PopoverAnchor asChild>
        <form
          ref={formRef}
          role="search"
          aria-label={filenamesOnly ? 'Search source files' : 'Search table'}
          className={`flex h-[28px] w-[220px] min-w-0 max-w-full items-center gap-1 rounded-md border border-brand-neutral-300 bg-transparent px-1 transition-colors focus-within:!border-brand-400 ${
            disabled ? 'opacity-50' : 'hover:border-brand-neutral-400'
          }`}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget) && !hasSearch && !text.trim()) {
              setDraft(null)
              setExpanded(false)
            }
          }}
          onSubmit={(event) => {
            event.preventDefault()
            if (disabled) return
            if (applied && navigation && !navigation.error) {
              navigation.step(1)
              return
            }
            setPopoverOpen(true)
            const search = text.trim()
            restoreFocus.current = !search
            onChange(search)
            setDraft(null)
            setExpanded(Boolean(search))
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && applied && navigation && !navigation.error) {
              event.preventDefault()
              navigation.step(event.shiftKey ? -1 : 1)
              return
            }
            if (event.key !== 'Escape') return
            event.preventDefault()
            event.stopPropagation()
            setDraft(null)
            setExpanded(false)
            restoreFocus.current = !hasSearch
          }}
        >
          <span className={`mr-0.5 shrink-0 rounded-full p-1 ${applied ? 'bg-brand-50 text-brand-500' : 'text-brand-neutral-600'}`}>
            <Search aria-hidden className="h-4 w-4" />
          </span>
          <input
            ref={(input) => {
              if (input && expanded && draft === null && !hasSearch) input.focus()
            }}
            type="search"
            aria-label={label}
            placeholder={label}
            value={text}
            disabled={disabled}
            onFocus={() => setPopoverOpen(true)}
            onChange={(event) => {
              setDraft(event.target.value)
              setPopoverOpen(true)
            }}
            className="m-0 min-w-0 flex-1 truncate border-0 bg-transparent p-0 text-sm text-brand-neutral-950 shadow-none placeholder:text-brand-neutral-500 focus:text-clip focus:outline-hidden focus:ring-0 [&::-webkit-search-cancel-button]:appearance-none"
          />
          {(text.length > 0 || hasSearch) && (
            <IconButton
              type="button"
              size="sm"
              variant="plain"
              color="neutral"
              icon={<XClear />}
              aria-label="Clear search"
              tooltipText="Clear search"
              disabled={disabled}
              onClick={() => {
                restoreFocus.current = true
                setDraft(null)
                setExpanded(false)
                if (hasSearch) onChange('')
              }}
            />
          )}
        </form>
      </PopoverAnchor>
      <PopoverContent
        width="trigger"
        align="start"
        showArrow={false}
        aria-label="Search status"
        className="flex min-h-12 max-w-[calc(100vw-32px)] items-center justify-between gap-3 border border-brand-neutral-200 px-3 py-2"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (event.target instanceof Node && formRef.current?.contains(event.target)) event.preventDefault()
        }}
      >
        <span role="status" className="min-w-0 flex-1 text-sm text-brand-neutral-600 tabular-nums">
          {hasEdits ? (
            <>
              Press{' '}
              <button
                type="button"
                aria-label="Run search (Enter)"
                className="inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded border border-brand-200 bg-brand-100 px-1.5 align-middle text-xs text-brand-800"
                onClick={() => formRef.current?.requestSubmit()}
              >
                <span aria-hidden>↵</span> Enter
              </button>{' '}
              to search
            </>
          ) : navigation?.error ? (
            'Search failed. Please retry.'
          ) : navigation?.searching ? (
            showSearching ? (
              'Searching…'
            ) : null
          ) : navigation?.noMatches ? (
            'No matches'
          ) : (
            <>
              <span className="text-brand-neutral-950">
                {navigation?.ordinal ? navigation.ordinal.toLocaleString() : 'Matches'}
                {navigation?.total != null ? ` of ${navigation.total.toLocaleString()}` : ''}
              </span>
              {navigation?.total == null &&
                (navigation?.countError ? (
                  <>
                    {' '}
                    ·{' '}
                    <button type="button" onClick={navigation.retryCount} className="underline">
                      Retry count
                    </button>
                  </>
                ) : showCounting ? (
                  ' · Counting…'
                ) : null)}
            </>
          )}
        </span>
        {!hasEdits && navigation && (
          <div className="flex shrink-0 gap-2">
            <IconButton
              type="button"
              size="sm"
              variant="plain"
              color="black"
              icon={<ChevronUp />}
              aria-label="Previous match (Shift+Enter)"
              disabled={!navigation.canPrevious}
              onClick={() => navigation.step(-1)}
            />
            <IconButton
              type="button"
              size="sm"
              variant="plain"
              color="black"
              icon={<ChevronDown />}
              aria-label="Next match (Enter)"
              disabled={!navigation.canNext}
              onClick={() => navigation.step(1)}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
